import { updateElementsOrderFromZIndex } from '@/utils/elementsUtils';
import type { Object as FabricObject } from 'fabric/fabric-impl';
import type { CanvasEx } from '@/typings/canvas';
import { ValueFormat } from '@/typings/designStudio/sourceElementTypes';
import {
    type PatternConfig,
    PATTERN_DEFAULTS,
    type PatternCell,
    type CellPosition,
    type PatternElement,
    type PatternLink,
    type PatternProductService,
    type PatternReplicaService,
    type CreatePatternHandlersParams
} from '@/typings/customilyElements/patternElements';


/** Parse the stored patternConfig (JSON string or object) into a full config, or null. */
export const parsePatternConfig = (value: string | PatternConfig | null): PatternConfig | null => {
    if (!value) return null;
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!parsed || typeof parsed !== 'object') return null;
        return { ...PATTERN_DEFAULTS, ...(parsed as Partial<PatternConfig>) };
    } catch {
        return null;
    }
};

const halfExtent = (total: number): number => Math.max(0, Math.floor((total - 1) / 2));

/**
 * Enumerate the grid cells for a config in a deterministic, stable order,
 * excluding the origin cell (0,0). The order must match between creation and re-sync so replicas map
 * to the same cell across operations.
 */
export const enumerateCells = (config: PatternConfig): PatternCell[] => {
    const rowExtent = halfExtent(config.rows);
    const colExtent = halfExtent(config.cols);
    const cells: PatternCell[] = [];
    for (let row = -rowExtent; row <= rowExtent; row++) {
        for (let col = -colExtent; col <= colExtent; col++) {
            if (row === 0 && col === 0) continue;
            cells.push({ row, col });
        }
    }
    return cells;
};

/** Center-to-center step between adjacent cells and the origin's on-canvas size, in pixels. */
const getGridSteps = (
    origin: FabricObject,
    config: PatternConfig
): { stepX: number; stepY: number; width: number; height: number } => {
    const width = origin.getScaledWidth();
    const height = origin.getScaledHeight();
    return {
        width,
        height,
        stepX: width * (1 + config.spacingX),
        stepY: height * (1 + config.spacingY)
    };
};

/** Absolute canvas center position for a single cell. */
export const computeCellPosition = (origin: FabricObject, config: PatternConfig, cell: PatternCell): CellPosition => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    const center = origin.getCenterPoint();
    const oddRowShift = cell.row % 2 !== 0 ? config.offsetX * width : 0;
    const oddColShift = cell.col % 2 !== 0 ? config.offsetY * height : 0;
    return {
        ...cell,
        left: center.x + cell.col * stepX + oddRowShift,
        top: center.y + cell.row * stepY + oddColShift
    };
};

export const getArtworkBounds = (canvas: CanvasEx): { width: number; height: number } => ({
    width: (canvas.getArtworkWidth() / canvas.getZoom()) * canvas.getEffectiveZoom(),
    height: (canvas.getArtworkHeight() / canvas.getZoom()) * canvas.getEffectiveZoom()
});

/**
 * Compute how many rows/cols are needed to fill the artwork: expand outward from the centered origin
 * while a cell still intersects the canvas at all; stop once the next ring is fully out of bounds.
 * A cell may cross the edge but is included as long as any part is inside.
 */
export const computeFillExtent = (
    origin: FabricObject,
    config: PatternConfig,
    canvasWidth: number,
    canvasHeight: number
): { rows: number; cols: number } => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    const center = origin.getCenterPoint();
    // Offsets stagger odd rows/cols by a fraction of the origin size; widen the half-extent by the
    // largest possible shift so the extent still covers the staggered cells and leaves no edge gaps.
    const maxShiftX = Math.abs(config.offsetX) * width;
    const maxShiftY = Math.abs(config.offsetY) * height;
    const halfW = width / 2 + maxShiftX;
    const halfH = height / 2 + maxShiftY;

    const intersectsX = (col: number) => {
        const centerX = center.x + col * stepX;
        return centerX + halfW > 0 && centerX - halfW < canvasWidth;
    };
    const intersectsY = (row: number) => {
        const centerY = center.y + row * stepY;
        return centerY + halfH > 0 && centerY - halfH < canvasHeight;
    };

    let colExtent = 0;
    if (stepX > 0) {
        while (intersectsX(colExtent + 1) || intersectsX(-(colExtent + 1))) colExtent++;
    }
    let rowExtent = 0;
    if (stepY > 0) {
        while (intersectsY(rowExtent + 1) || intersectsY(-(rowExtent + 1))) rowExtent++;
    }

    return { rows: rowExtent * 2 + 1, cols: colExtent * 2 + 1 };
};

/** Element as loaded: patternConfig still holds the saved JSON string and replicas may carry a stroke backup */
type LoadedPatternElement = PatternElement & {
    patternConfig?: PatternConfig | string | null;
    _backupStroke?: string;
    setStroke?: (stroke: string) => void;
};

/**
 * Rebuilds the in-memory pattern state of a loaded product: only the origin's patternConfig and the links are saved,
 *  the pattern/replica flags and each replica's cell come from them. `onReplicaLinked` lets each element type finish
 *  wiring its replicas. `keepSavedLock` is for previews (step 2), the only place a replica can be unlocked.
 */
export const populatePatternLinks = (
    elementsLinks: PatternLink[],
    canvas: CanvasEx,
    onReplicaLinked?: (origin: PatternElement, replica: PatternElement) => void,
    { keepSavedLock = false }: { keepSavedLock?: boolean } = {}
) => {
    const patternLinks = elementsLinks.filter((link) => link.valueFormat === ValueFormat.Pattern);
    if (!patternLinks.length) return;

    const objects = canvas.getObjects() as LoadedPatternElement[];
    const linksByOrigin = new Map<string, PatternLink[]>();
    for (const link of patternLinks) {
        if (!linksByOrigin.has(link.sourceId)) linksByOrigin.set(link.sourceId, []);
        linksByOrigin.get(link.sourceId)!.push(link);
    }

    for (const [originId, links] of linksByOrigin) {
        const origin = objects.find((obj) => obj.uuid === originId);
        if (!origin) continue;

        const config = parsePatternConfig(origin.patternConfig ?? null);
        origin.pattern = true;
        origin.patternConfig = config;
        const cells = config ? enumerateCells(config) : [];

        links.forEach((link, index) => {
            const replica = objects.find((obj) => obj.uuid === link.targetId);
            if (!replica) return;
            replica.isPatternReplica = true;
            replica.patternOriginUuid = originId;
            if (!keepSavedLock) replica.set('locked', true);
            // Replicas never show the red "locked" border updateLockStatus may have painted
            if (replica._backupStroke !== undefined) {
                if (replica.setStroke) replica.setStroke(replica._backupStroke);
                else replica.set('stroke', replica._backupStroke);
            }
            if (cells[index]) {
                replica.patternCell = { row: cells[index].row, col: cells[index].col };
            }
            onReplicaLinked?.(origin, replica);
        });
    }
};

/** Types that can originate a pattern; which image placeholders qualify is narrowed by the properties panel */
export const isPatternOriginType = (object?: { type?: string } | null): boolean =>
    object?.type === 'bounded-text' || object?.type === 'image-placeholder';

/** Replicas of an origin, in link order (the order enumerateCells assigned them). */
export const getPatternReplicaObjects = (
    canvas: CanvasEx,
    ProductService: Pick<PatternProductService, 'getPatternReplicaLinks'>,
    originUuid: string
): PatternElement[] => {
    const objects = canvas.getObjects() as PatternElement[];
    return ProductService.getPatternReplicaLinks(originUuid)
        .map((link) => objects.find((o) => o.uuid === link.targetId))
        .filter((o): o is PatternElement => !!o);
};

/** An unlocked replica (step 2) is detached from its origin until it's locked again */
export const followsOrigin = (replica: PatternElement) => replica.locked !== false;

// setTimeout and not requestAnimationFrame: a hidden tab would stop the batches
const yieldToBrowser = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

export const createPatternHandlers = ({
    getCanvas,
    $scope,
    ProductService,
    getReplicaService
}: CreatePatternHandlersParams) => {
    const markEdited = (origin: PatternElement, keys: string[]) => {
        if ($scope.editMode) origin.setEditedProp(...keys);
    };

    /**
     * Runs the work in the service's batches, rendering the progress between them; null when a newer sync made
     *  this one stale. Services without batchSize run everything at once, as the text pattern always did.
     */
    const runInBatches = async <T, R>(
        items: T[],
        { batchSize, batchTimeBudgetMs }: Pick<PatternReplicaService, 'batchSize' | 'batchTimeBudgetMs'>,
        run: (item: T, index: number) => R | Promise<R>,
        isStale: () => boolean = () => false
    ): Promise<R[] | null> => {
        if (!batchSize) return Promise.all(items.map(run));
        const results: R[] = [];
        // Each yield costs a render and whatever else is queued (tens of ms): cheap batches share one
        let lastYield = performance.now();
        for (let start = 0; start < items.length; start += batchSize) {
            if (isStale()) return null;
            const batch = items.slice(start, start + batchSize);
            results.push(...(await Promise.all(batch.map((item, i) => run(item, start + i)))));
            const overBudget = !batchTimeBudgetMs || performance.now() - lastYield >= batchTimeBudgetMs;
            if (start + batchSize < items.length && overBudget) {
                getCanvas().requestRenderAll();
                await yieldToBrowser();
                lastYield = performance.now();
            }
        }
        return results;
    };

    // Removed, or replaced by a reload with the same uuids (another preview): its replicas are someone else's now
    const isOnCanvas = (origin: PatternElement) => getCanvas().getObjects().includes(origin as never);

    // A newer resync of the same origin supersedes the one still going through its batches, and so does its removal
    const syncGenerations = new Map<string, number>();
    const startSyncGeneration = (origin: PatternElement, batchSize: number | undefined) => {
        if (!batchSize) return () => !origin.pattern || !isOnCanvas(origin);
        const uuid = origin.uuid as string;
        const generation = (syncGenerations.get(uuid) ?? 0) + 1;
        syncGenerations.set(uuid, generation);
        return () => syncGenerations.get(uuid) !== generation || !origin.pattern || !isOnCanvas(origin);
    };

    // Batched builds link their replicas only once every batch is done: a resync meanwhile would build them again
    const buildsInProgress = new Map<string, Promise<void>>();
    const trackBuild = async (origin: PatternElement, batchSize: number | undefined, build: () => Promise<void>) => {
        if (!batchSize) return build();
        const uuid = origin.uuid as string;
        const done = build();
        const settled = done.catch(() => undefined);
        buildsInProgress.set(uuid, settled);
        try {
            await done;
        } finally {
            if (buildsInProgress.get(uuid) === settled) buildsInProgress.delete(uuid);
        }
    };
    const waitForBuilds = async (origin: PatternElement) => {
        const uuid = origin.uuid as string;
        for (let build = buildsInProgress.get(uuid); build; build = buildsInProgress.get(uuid)) await build;
    };

    const getReplicas = (origin: PatternElement) =>
        getPatternReplicaObjects(getCanvas(), ProductService, origin.uuid as string);

    const getMinZIndex = (canvas: CanvasEx) => {
        const zIndices = canvas.getObjects().map((o) => o.zIndex);
        return zIndices.length ? Math.min(...zIndices) : 0;
    };

    const renumberReplicaZIndices = (origin: PatternElement) => {
        getReplicas(origin).forEach((replica, i) => replica.set('zIndex', origin.zIndex + 1 + i));
    };

    const createReplicasForCells = async (
        origin: PatternElement,
        config: PatternConfig,
        cells: PatternCell[],
        reuseUuids: (string | undefined)[]
    ) => {
        const canvas = getCanvas();
        const service = getReplicaService(origin);
        await trackBuild(origin, service.batchSize, async () => {
            const replicas =
                (await runInBatches(cells, service, (cell, i) => {
                    const pos = computeCellPosition(origin, config, cell);
                    return service.buildPatternReplica(canvas, origin, cell, {
                        uuid: reuseUuids[i],
                        left: pos.left,
                        top: pos.top,
                        zIndex: origin.zIndex + 1,
                        editMode: $scope.editMode
                    });
                })) ?? [];
            // Linked after every build resolves so the link order keeps matching the cell order
            replicas.forEach((replica) =>
                ProductService.addPatternElementsLink(origin.uuid as string, replica.uuid as string, service.elementType)
            );
        });
    };

    const reconcileReplicas = async (
        origin: PatternElement,
        config: PatternConfig,
        cells: PatternCell[],
        isStale: () => boolean = () => false
    ) => {
        const canvas = getCanvas();
        const service = getReplicaService(origin);
        const replicas = getReplicas(origin);
        const shared = replicas.slice(0, cells.length);
        const synced = await runInBatches(
            shared,
            service,
            (replica, i) => {
                replica.patternCell = { row: cells[i].row, col: cells[i].col };
                if (!followsOrigin(replica)) return;
                const pos = computeCellPosition(origin, config, cells[i]);
                return service.syncPatternReplica(canvas, origin, replica, pos);
            },
            isStale
        );
        // Stale after its last batch too (e.g. removePattern meanwhile): creating replicas would bring the pattern back
        if (!synced || isStale()) return;
        if (cells.length > replicas.length) {
            await createReplicasForCells(origin, config, cells.slice(replicas.length), []);
        } else if (replicas.length > cells.length) {
            for (const extra of replicas.slice(cells.length)) {
                ProductService.removePatternElementsLinkByReplica(extra.uuid as string);
                canvas.remove(extra);
            }
        }
        renumberReplicaZIndices(origin);
    };

    const centerOrigin = (canvas: CanvasEx, origin: PatternElement) => {
        const center = canvas.getCenter();
        origin.set({ left: center.left, top: center.top });
        origin.setCoords();
    };

    const createPattern = async (origin: PatternElement) => {
        const canvas = getCanvas();
        centerOrigin(canvas, origin);

        const config: PatternConfig = { ...PATTERN_DEFAULTS };
        const bounds = getArtworkBounds(canvas);
        const extent = computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;

        origin.set('zIndex', getMinZIndex(canvas) - 1);
        origin.set('pattern', true);
        origin.set('patternConfig', config);
        markEdited(origin, ['pattern', 'patternConfig', 'zIndex']);

        await createReplicasForCells(origin, config, enumerateCells(config), []);
        renumberReplicaZIndices(origin);
        updateElementsOrderFromZIndex(canvas);
        canvas.setActiveObject(origin);
        canvas.requestRenderAll();
    };

    const repositionReplicasNow = (origin: PatternElement, resize: boolean) => {
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config) return;

        const service = getReplicaService(origin);
        const cells = enumerateCells(config);
        const replicas = getReplicas(origin);
        const count = Math.min(cells.length, replicas.length);
        for (let i = 0; i < count; i++) {
            replicas[i].patternCell = { row: cells[i].row, col: cells[i].col };
            if (!followsOrigin(replicas[i])) continue;
            const pos = computeCellPosition(origin, config, cells[i]);
            service.repositionPatternReplica(origin, replicas[i], pos, resize);
        }
        canvas.requestRenderAll();
    };

    let repositionFrame: number | null = null;
    // Per origin: a multi-selection repositions several in the same frame
    const pendingReposition = new Map<PatternElement, boolean>();
    const repositionReplicas = (origin: PatternElement, options: { resize?: boolean } = {}) => {
        pendingReposition.set(origin, !!pendingReposition.get(origin) || !!options.resize);
        if (repositionFrame !== null) return;
        repositionFrame = requestAnimationFrame(() => {
            repositionFrame = null;
            const pending = Array.from(pendingReposition);
            pendingReposition.clear();
            for (const [originToSync, resize] of pending) repositionReplicasNow(originToSync, resize);
        });
    };

    const resyncReplicas = async (origin: PatternElement) => {
        if (buildsInProgress.has(origin.uuid as string)) await waitForBuilds(origin);
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config || !isOnCanvas(origin)) return;

        const service = getReplicaService(origin);
        const isStale = startSyncGeneration(origin, service.batchSize);
        const cells = enumerateCells(config);
        const replicas = getReplicas(origin);

        if (cells.length !== replicas.length) {
            await reconcileReplicas(origin, config, cells, isStale);
        } else {
            await runInBatches(
                replicas,
                service,
                (replica, i) => {
                    replica.patternCell = { row: cells[i].row, col: cells[i].col };
                    if (!followsOrigin(replica)) return;
                    const pos = computeCellPosition(origin, config, cells[i]);
                    replica.set('zIndex', origin.zIndex + 1 + i);
                    return service.syncPatternReplica(canvas, origin, replica, pos);
                },
                isStale
            );
        }
        if (isStale()) return;
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    const refreshFill = async (origin: PatternElement) => {
        if (buildsInProgress.has(origin.uuid as string)) await waitForBuilds(origin);
        if (!isOnCanvas(origin)) return;
        const canvas = getCanvas();
        centerOrigin(canvas, origin);

        const config: PatternConfig = { ...(origin.patternConfig || PATTERN_DEFAULTS) };
        const bounds = getArtworkBounds(canvas);
        const extent = computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);

        const isStale = startSyncGeneration(origin, getReplicaService(origin).batchSize);
        await reconcileReplicas(origin, config, enumerateCells(config), isStale);
        if (isStale()) return;
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    const updateConfig = (origin: PatternElement, partial: Partial<PatternConfig>) => {
        const current = origin.patternConfig || PATTERN_DEFAULTS;
        const config: PatternConfig = { ...current, ...partial };
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);
        repositionReplicas(origin);
    };

    const removeReplicasOf = (originUuid: string) => {
        const canvas = getCanvas();
        for (const replica of getPatternReplicaObjects(canvas, ProductService, originUuid)) canvas.remove(replica);
        ProductService.removePatternElementsLinks(originUuid);
    };

    const removePattern = (origin: PatternElement) => {
        const canvas = getCanvas();
        removeReplicasOf(origin.uuid as string);

        origin.set('pattern', false);
        origin.set('patternConfig', null);
        markEdited(origin, ['pattern', 'patternConfig']);
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    // Replicas have no undo entries of their own: an origin taken off the canvas keeps them to come back with them
    const detachedPatterns = new WeakMap<PatternElement, PatternElement[]>();

    /** The origin left the canvas (deleted, or its add undone): its replicas and links go, kept for reattachPattern */
    const detachPattern = (origin?: PatternElement | null) => {
        // A reload (preview switch, new background) clears the canvas through removals: the pattern stays in the product
        if (getCanvas().isClearing) return;
        if (!origin || !isPatternOriginType(origin) || !origin.pattern) return;
        detachedPatterns.set(origin, getReplicas(origin));
        removeReplicasOf(origin.uuid as string);
    };

    /** The same origin is back (redo of its add, undo of its removal): so are its replicas, in their link order */
    const reattachPattern = (origin?: PatternElement | null) => {
        const replicas = origin ? detachedPatterns.get(origin) : undefined;
        if (!origin || !replicas) return;
        detachedPatterns.delete(origin);
        if (!origin.pattern || getReplicas(origin).length) return;
        const canvas = getCanvas();
        const { elementType } = getReplicaService(origin);
        for (const replica of replicas) {
            canvas.addWithoutHistory(replica);
            ProductService.addPatternElementsLink(origin.uuid as string, replica.uuid as string, elementType);
        }
        renumberReplicaZIndices(origin);
        updateElementsOrderFromZIndex(canvas);
        repositionReplicas(origin, { resize: true });
    };

    /** Drops the replicas/links of deleted elements. Only uuids survive the removal, so links tell origins apart. */
    const cleanupDeletedPatterns = (deleted: { uuid?: string }[] | null | undefined) => {
        if (!deleted?.length) return;
        for (const { uuid } of deleted) {
            if (!uuid) continue;
            if (ProductService.getPatternReplicaLinks(uuid).length) {
                removeReplicasOf(uuid);
            } else {
                ProductService.removePatternElementsLinkByReplica(uuid);
            }
        }
        updateElementsOrderFromZIndex(getCanvas());
    };

    return {
        createPattern,
        repositionReplicas,
        resyncReplicas,
        refreshFill,
        updateConfig,
        removePattern,
        cleanupDeletedPatterns,
        detachPattern,
        reattachPattern
    };
};
